import React, { useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import EntryListItem from './EntryListItem';
import type { Entry, EntryListItemProps } from '../types';
import { entryTimeMs, sortByObservedDesc } from '../model/entryMeta';
import { NOTE_TEMPLATES } from '../../../../constants/noteTemplates';

type ItemProps = Omit<EntryListItemProps, 'entry'>;

export interface EntryHourGroup {
  key: string;
  label: string;
  entries: Entry[];
}

/** Phase 18 M8: entries grouped by the hour of observedAt, newest first */
export const groupEntriesByHour = (entries: Entry[]): EntryHourGroup[] => {
  const groups: EntryHourGroup[] = [];
  for (const entry of sortByObservedDesc(entries.filter(Boolean))) {
    const ms = entryTimeMs(entry);
    const d = new Date(ms);
    const key = ms ? `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}-${d.getHours()}` : 'unknown';
    let group = groups[groups.length - 1];
    if (!group || group.key !== key) {
      const label = ms ? d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric' }) : 'Time not recorded';
      group = { key, label, entries: [] };
      groups.push(group);
    }
    group.entries.push(entry);
  }
  return groups;
};

/** Phase 18U U10: filter chips over the entry types */
export const ENTRY_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'vitals', label: 'Vitals' },
  { key: 'meds', label: 'Meds' },
  { key: 'resp', label: 'Resp' },
  { key: 'notes', label: 'Notes' },
  { key: 'other', label: 'Other' },
] as const;
export type EntryFilter = (typeof ENTRY_FILTERS)[number]['key'];

const FILTER_TYPES: Record<Exclude<EntryFilter, 'all' | 'other'>, readonly string[]> = {
  vitals: ['vitals', 'measurements'],
  meds: ['mar'],
  resp: ['respiratory'],
  // Phase 20E note templates (social work, RT, nutrition, behavioral, symptoms) count as notes
  notes: ['notes', 'summary', 'addendum', 'supervisory', 'therapy', 'aidetasks', ...NOTE_TEMPLATES.map((t) => t.entryType.toLowerCase())],
};
const NAMED = new Set(Object.values(FILTER_TYPES).flat());

export const matchesFilter = (entry: Entry, filter: EntryFilter): boolean => {
  if (filter === 'all') return true;
  if (filter === 'other') return !NAMED.has(entry.type);
  return FILTER_TYPES[filter].includes(entry.type);
};

/** Groups open at first: the two newest hours (U-Q3: the current and the previous hour) */
export const OPEN_GROUPS = 2;

/**
 * The visit's entries, one section per hour of observed time. Phase 18U U10: older hours are
 * collapsed (with a count) until opened, filter chips narrow the list, and the hour headers stick
 * below the screen's own sticky header (`--chart-sticky-top`, set by the visit screen).
 */
const EntryList: React.FC<ItemProps & { entries: Entry[]; keyPrefix?: string }> = ({ entries, keyPrefix = 'entry', ...itemProps }) => {
  const [filter, setFilter] = useState<EntryFilter>('all');
  const [toggled, setToggled] = useState<Record<string, boolean>>({});
  const visible = useMemo(() => entries.filter((e) => e && matchesFilter(e, filter)), [entries, filter]);
  const groups = groupEntriesByHour(visible);
  const presentTypes = new Set(entries.filter(Boolean).map((e) => e.type));
  const chips = ENTRY_FILTERS.filter((f) => f.key === 'all' || entries.some((e) => e && matchesFilter(e, f.key)));
  // The highlighted (just saved) entry's hour always opens
  const highlightKey = itemProps.highlightEntryId != null
    ? groups.find((g) => g.entries.some((e) => String(e.id) === String(itemProps.highlightEntryId)))?.key
    : undefined;
  // An entry being edited keeps its hour open (its edit sheet is rendered by the row)
  const editKey = itemProps.inPlaceEditingEntryId != null
    ? groups.find((g) => g.entries.some((e) => String(e.id) === String(itemProps.inPlaceEditingEntryId)))?.key
    : undefined;
  const isOpen = (g: EntryHourGroup, index: number) => g.key === editKey || (toggled[g.key] ?? (index < OPEN_GROUPS || g.key === highlightKey || filter !== 'all'));
  const anyClosed = groups.some((g, i) => !isOpen(g, i));

  return (
    <div className="space-y-3" data-testid="entry-list">
      {(presentTypes.size > 1 || groups.length > OPEN_GROUPS) && (
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Show entries">
          {chips.length > 2 && chips.map((f) => (
            <button
              key={f.key}
              type="button"
              aria-pressed={filter === f.key}
              onClick={() => setFilter(f.key)}
              className={`min-h-[36px] rounded-full border px-3 text-sm font-medium ${filter === f.key ? 'border-slate-800 bg-slate-800 text-white' : 'border-gray-300 bg-white text-gray-700'}`}
            >
              {f.label}
            </button>
          ))}
          {groups.length > OPEN_GROUPS && (
            <button
              type="button"
              onClick={() => setToggled(Object.fromEntries(groups.map((g) => [g.key, anyClosed])))}
              className="ml-auto min-h-[36px] px-2 text-sm font-medium text-sky-800 underline"
            >
              {anyClosed ? 'Expand all' : 'Collapse older'}
            </button>
          )}
        </div>
      )}
      {groups.length === 0 && <p className="text-sm text-gray-600">No entries of this kind yet.</p>}
      {groups.map((group, index) => {
        const open = isOpen(group, index);
        return (
          <section key={group.key} aria-label={group.label} className="space-y-1">
            <h5 className="sticky top-[var(--chart-sticky-top,0px)] z-[1] m-0 bg-white/95" data-testid="entry-hour">
              <button
                type="button"
                aria-expanded={open}
                onClick={() => setToggled((t) => ({ ...t, [group.key]: !open }))}
                className="flex min-h-[36px] w-full items-center justify-between text-left text-xs font-semibold uppercase tracking-wide text-gray-700"
              >
                <span>{group.label}</span>
                <span className="flex items-center gap-1 normal-case">
                  {group.entries.length} {group.entries.length === 1 ? 'entry' : 'entries'}
                  <ChevronDown size={16} aria-hidden="true" className={`transition-transform ${open ? 'rotate-180' : ''}`} />
                </span>
              </button>
            </h5>
            {open && group.entries.map((entry) => (
              <EntryListItem key={`${keyPrefix}-${entry.id}`} entry={entry} {...itemProps} />
            ))}
          </section>
        );
      })}
    </div>
  );
};

export default EntryList;
